import * as React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import { formatPrice } from 'modules/billing/lib/prices'
import { usePricingBundleContext } from 'modules/onboarding/components/Pricing/PricingBundleContext'
import { MIN_SEATS } from 'modules/onboarding/lib/pricing/plans/utils'
import { Currency, Tier } from 'modules/onboarding/lib/pricing/types'

const DEFAULT_INCLUDE_SEATS = 10

export type OrderItem = {
  name: string
  isYearly: boolean
  perSeatPrice: number
  feePrice: number
  startingPrice?: number
  includedSeats?: number
  tieredPrice?: Tier[]
}

interface Props {
  orderItem: OrderItem
  currency: Currency
}

const ItemInfo = ({ orderItem, currency }: Props) => {
  const { addons } = usePricingBundleContext()
  const {
    name,
    isYearly,
    perSeatPrice,
    feePrice,
    startingPrice,
    includedSeats = DEFAULT_INCLUDE_SEATS,
    tieredPrice,
  } = orderItem
  const price = startingPrice || (perSeatPrice ? perSeatPrice * MIN_SEATS : feePrice)
  const totalPrice = startingPrice || perSeatPrice * MIN_SEATS + feePrice
  const yearlyPrice = startingPrice || (feePrice ? price * 12 + feePrice : price * 12)
  // The first tier of a plan have a price per seat of 0. We can take the endingUnit of this tier to figure out the starting seats.
  const includedSeatsPerModule = tieredPrice?.[0]?.endingUnit
  // The last tier is where we can find the price per seat of all those seats above the "starting seats".
  const pricePerUser = tieredPrice?.[tieredPrice?.length - 1]?.price || 0

  return (
    <Box gap="s2">
      <Box flexDirection="row" justifyContent="spaceBetween" gap="s12">
        <Text.Normal color="grey1000" weight="medium">
          {name}
        </Text.Normal>
        <Text.Normal color="grey1000" weight="medium" ellipsis>
          {formatPrice(isYearly ? yearlyPrice : totalPrice, currency)}
        </Text.Normal>
      </Box>
      {perSeatPrice ? (
        <>
          <Text.Normal size="200" color="grey700">
            {formatPrice(price, currency)} /
            {isYearly ? i18n.t('common.year') : i18n.t('common.month')}{' '}
            {i18n.t('pricing_plans.employees_inludede', { employees: includedSeats })}
          </Text.Normal>
          <Text.Normal size="200" color="grey700">
            {formatPrice(perSeatPrice, currency)} /
            {isYearly ? i18n.t('common.year') : i18n.t('common.month')}{' '}
            {i18n.t('pricing_plans.billing_information.summary_card.per_new_employee')}
          </Text.Normal>
        </>
      ) : null}
      {feePrice ? (
        <Text.Normal size="200" color="grey700">
          {formatPrice(feePrice, currency)} /
          {isYearly ? i18n.t('common.year') : i18n.t('common.month')}
          {', '}
          {i18n.t('pricing_plans.billing_information.summary_card.fixed_fee')}
          {includedSeatsPerModule && (
            <>
              {`, ${i18n.t('pricing_plans.employees_inludede', {
                employees: includedSeatsPerModule,
              })}`}
            </>
          )}
        </Text.Normal>
      ) : null}
      {addons.length ? (
        <Box paddingTop="s16">
          <Text.Normal size="200" color="grey800">
            {i18n.t('pricing_plans.billing_information.summary_card.included')}
          </Text.Normal>
          <Box paddingTop="s8">
            {addons.map(({ title }) => (
              <Text.Normal key={title} color="grey1000" weight="medium">
                {title}
              </Text.Normal>
            ))}
          </Box>
        </Box>
      ) : null}
      {tieredPrice ? (
        <Text.Normal size="200" color="grey800">
          {i18n.t('pricing_plans.per_new_employee', {
            amount: formatPrice(pricePerUser / 100, currency),
          })}
        </Text.Normal>
      ) : null}
    </Box>
  )
}

export default ItemInfo
